<template>
  <div class="owner-page">
    <section class="owner-hero" aria-labelledby="owner-title">
      <img src="/img/public-home-inspection.webp" alt="业主新房验房与收房陪验服务现场" />
      <div class="owner-hero-mask"></div>
      <div class="owner-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <span>业主验房</span>
        </nav>
        <p class="eyebrow">Owner Home Inspection</p>
        <h1 id="owner-title">业主新房验房与收房陪验服务</h1>
        <p>
          面向个人业主提供精装房、毛坯房、新房交付前后的第三方验房服务，协助业主发现墙面、地面、水电、门窗、厨卫、空鼓、渗漏等常见质量问题，并形成清晰的问题记录和整改建议。
        </p>
        <div class="hero-actions">
          <a class="btn btn-primary" href="#owner-contact">预约业主验房</a>
          <a class="btn btn-secondary" href="#owner-items">查看验房内容</a>
        </div>
      </div>
    </section>

    <section class="section positioning-section">
      <div class="section-inner positioning-layout">
        <div>
          <p class="eyebrow">Clear Positioning</p>
          <h2>对私验房是独立服务，不替代首页的对公定位</h2>
          <p>
            江苏道鼎升当前首页和主服务以对公工程查验、精装入场查验和住宅项目质量过程管控为主。个人业主验房单独展示，便于业主了解新房验房、精装房验房、毛坯房验房和收房陪验的服务范围。
          </p>
        </div>
        <div class="notice-card">
          <strong>真实性说明</strong>
          <p>当前页面不展示虚构业主案例、客户评价、评分、成交数量或项目图片。后续如有真实授权资料，可再补充展示。</p>
        </div>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Applicable Scenes</p>
          <h2>适用场景</h2>
        </div>
        <div class="scenario-grid">
          <article v-for="item in service.scenarios" :key="item">
            <i class="el-icon-house"></i>
            <h3>{{ item }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section id="owner-items" class="section">
      <div class="section-inner items-layout">
        <div>
          <p class="eyebrow">Inspection Items</p>
          <h2>常见查验项目</h2>
          <p>验房范围会根据房屋状态、精装或毛坯类型、现场条件和业主关注点确认。</p>
          <ul class="check-list">
            <li v-for="item in service.checkItems" :key="item">
              <i class="el-icon-check"></i>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
        <img src="/img/public-home-inspection.webp" alt="专业验房人员与业主查看新房质量问题" />
      </div>
    </section>

    <section class="section process-section">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Workflow</p>
          <h2>服务流程</h2>
          <p>预约咨询 → 确认房屋情况 → 现场查验 → 问题记录 → 整改建议 → 可选复查</p>
        </div>
        <ol class="process-list">
          <li v-for="(step, index) in process" :key="step.title">
            <span>{{ index + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner deliver-layout">
        <div>
          <p class="eyebrow">Deliverables</p>
          <h2>交付内容</h2>
          <p>交付内容以协助业主清楚理解问题、记录问题并沟通整改为目标。</p>
        </div>
        <ul class="deliver-list">
          <li v-for="item in service.deliverables" :key="item">
            <i class="el-icon-document-checked"></i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section class="section">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">FAQ</p>
          <h2>业主验房常见问题</h2>
        </div>
        <div class="faq-list">
          <details v-for="item in service.faqs" :key="item.question">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section id="owner-contact" class="section cta-section">
      <div class="section-inner cta-inner">
        <div>
          <h2>预约业主验房或整改复查</h2>
          <p>请提供房屋所在城市、小区名称、户型、精装或毛坯状态、预计收房时间和关注问题，我们会进一步沟通可预约时间。</p>
        </div>
        <div class="contact-card">
          <p><strong>电话：</strong>{{ company.phone }}</p>
          <p><strong>邮箱：</strong>{{ company.email }}</p>
          <router-link class="btn btn-primary" to="/download">提交预约信息</router-link>
        </div>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">{{ company.icp }}</a>
      <a :href="company.publicSecurityUrl" target="_blank" rel="noopener">{{ company.publicSecurityRecord }}</a>
    </div>
  </div>
</template>

<script>
import { companyInfo, ownerInspectionService, ownerProcessDetails } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  faqSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "OwnerInspection",
  data() {
    return {
      company: companyInfo,
      service: ownerInspectionService,
      process: ownerProcessDetails
    };
  },
  mounted() {
    setSeo({
      title: "业主验房与收房陪验服务｜新房验房｜江苏道鼎升工程检测",
      description: this.service.metaDescription,
      path: this.service.path,
      image: this.service.image
    });
    setStructuredData([
      organizationSchema(),
      localBusinessSchema(),
      {
        "@type": "Service",
        "@id": absoluteUrl(this.service.path + "#service"),
        name: this.service.title,
        serviceType: this.service.serviceType,
        description: this.service.summary,
        provider: {
          "@id": absoluteUrl("/#organization")
        },
        areaServed: companyInfo.serviceArea,
        url: absoluteUrl(this.service.path)
      },
      faqSchema(this.service.faqs),
      breadcrumbSchema([
        { name: "首页", path: "/" },
        { name: "业主验房", path: this.service.path }
      ])
    ]);
  }
};
</script>

<style lang="scss" scoped>
.owner-page {
  background: #f4f6f8;
  color: #1f2d3d;
}

.owner-hero {
  min-height: 500px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.owner-hero img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.owner-hero-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(7, 25, 40, 0.92), rgba(12, 58, 83, 0.68), rgba(12, 58, 83, 0.25));
}

.owner-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.owner-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  gap: 8px;
  margin-bottom: 26px;
  color: rgba(255, 255, 255, 0.78);
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
}

.eyebrow {
  color: #c8872a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.owner-hero h1 {
  max-width: 860px;
  font-size: 44px;
  line-height: 1.24;
  margin: 0 0 18px;
}

.owner-hero p {
  max-width: 900px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
}

.btn {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  border-radius: 4px;
  color: #fff;
  text-decoration: none;
  font-weight: 700;
}

.btn-primary {
  background: #0c4a6e;
}

.btn-secondary {
  background: #c8872a;
}

.section {
  padding: 76px 0;
}

.muted {
  background: #fff;
}

.positioning-layout,
.items-layout,
.deliver-layout,
.cta-inner {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 40px;
  align-items: start;
}

h2 {
  color: #102d42;
  font-size: 32px;
  line-height: 1.35;
  margin: 0 0 14px;
}

.positioning-layout p,
.section-heading p,
.items-layout p,
.deliver-layout p,
.cta-section p,
.faq-list p {
  color: #5e6b76;
  line-height: 1.8;
}

.notice-card,
.scenario-grid article,
.check-list li,
.deliver-list li,
.faq-list details,
.contact-card {
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  background: #fff;
}

.notice-card {
  padding: 26px;
  box-shadow: 0 18px 40px rgba(15, 45, 66, 0.06);
}

.notice-card strong {
  display: block;
  color: #0c4a6e;
  font-size: 20px;
  margin-bottom: 10px;
}

.section-heading {
  max-width: 780px;
  margin-bottom: 32px;
}

.scenario-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.scenario-grid article {
  min-height: 148px;
  padding: 22px;
}

.scenario-grid i {
  color: #c8872a;
  font-size: 24px;
  margin-bottom: 16px;
}

.scenario-grid h3 {
  color: #102d42;
  font-size: 18px;
  line-height: 1.5;
  margin: 0;
}

.items-layout {
  align-items: center;
}

.items-layout img {
  width: 100%;
  min-height: 440px;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  box-shadow: 0 22px 48px rgba(15, 45, 66, 0.12);
}

.check-list,
.deliver-list,
.process-list {
  list-style: none;
  padding: 0;
  margin: 24px 0 0;
}

.check-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.check-list li,
.deliver-list li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 16px;
  color: #3d4b56;
  line-height: 1.7;
}

.check-list i,
.deliver-list i {
  color: #0c4a6e;
  margin-top: 3px;
}

.process-section {
  background: #102d42;
  color: #fff;
}

.process-section h2,
.process-section .section-heading p {
  color: #fff;
}

.process-section .section-heading p {
  color: rgba(255, 255, 255, 0.78);
}

.process-list {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.process-list li {
  min-height: 170px;
  padding: 22px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
}

.process-list span {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  background: #c8872a;
  color: #fff;
  font-weight: 700;
}

.process-list h3 {
  color: #fff;
  margin: 16px 0 8px;
  font-size: 18px;
}

.process-list p {
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
  line-height: 1.7;
}

.deliver-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 0;
}

.faq-list {
  display: grid;
  gap: 12px;
}

.faq-list details {
  padding: 0 22px;
}

.faq-list summary {
  cursor: pointer;
  list-style: none;
  padding: 20px 0;
  color: #102d42;
  font-size: 17px;
  font-weight: 700;
}

.faq-list summary::-webkit-details-marker {
  display: none;
}

.faq-list p {
  border-top: 1px solid #edf2f1;
  padding: 16px 0 20px;
  margin: 0;
}

.cta-section {
  background: #eef3f7;
}

.contact-card {
  padding: 26px;
}

.contact-card p {
  margin: 0 0 12px;
  color: #102d42;
}

@media (max-width: 1080px) {
  .positioning-layout,
  .items-layout,
  .deliver-layout,
  .cta-inner,
  .scenario-grid,
  .check-list,
  .process-list,
  .deliver-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .owner-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .owner-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .owner-hero h1 {
    font-size: 30px;
  }

  .section {
    padding: 56px 0;
  }

  h2 {
    font-size: 26px;
  }

  .btn {
    width: 100%;
  }

  .items-layout img {
    min-height: 280px;
  }
}
</style>
